import React, { useState } from 'react'
import styles from './MicrositeImageGalleryManager.module.css'
import { GripVertical, X, Image as ImageIcon, Plus } from 'lucide-react'

/**
 * MicrositeImageGalleryManager
 * Multi-image add/remove/reorder manager — extracted from
 * MicrositeGeneral's inline gallery section so it can be reused
 * anywhere multiple image URLs need managing (e.g. About section,
 * Categories section).
 *
 * Usage:
 *   <MicrositeImageGalleryManager
 *     images={form.storeImages}
 *     onChange={(next) => updateField('storeImages', next)}
 *   />
 */
const MicrositeImageGalleryManager = ({ images = [], onChange, addPlaceholder = 'Paste an image URL and press Enter' }) => {
    const [newUrl, setNewUrl] = useState('')
    const [dragIdx, setDragIdx] = useState(null)

    const addImage = () => {
        const url = newUrl.trim()
        if (!url) return
        onChange?.([...images, url])
        setNewUrl('')
    }

    const removeImage = (idx) => {
        onChange?.(images.filter((_, i) => i !== idx))
    }

    const handleDrop = (idx) => {
        if (dragIdx === null || dragIdx === idx) {
            setDragIdx(null)
            return
        }
        const next = [...images]
        const [moved] = next.splice(dragIdx, 1)
        next.splice(idx, 0, moved)
        onChange?.(next)
        setDragIdx(null)
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.grid}>
                {images.map((url, idx) => (
                    <div
                        key={idx}
                        className={`${styles.tile} ${dragIdx === idx ? styles.tileDragging : ''}`}
                        draggable
                        onDragStart={() => setDragIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDrop(idx)}
                    >
                        <GripVertical size={14} className={styles.dragHandle} />
                        <img
                            src={url}
                            alt={`Gallery ${idx + 1}`}
                            className={styles.thumb}
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                        />
                        <span className={styles.urlText} title={url}>{url}</span>
                        <button type="button" className={styles.removeBtn} onClick={() => removeImage(idx)}>
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>

            <div className={styles.addRow}>
                <ImageIcon size={14} className={styles.addIcon} />
                <input
                    type="text"
                    className={styles.addInput}
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addImage()}
                    placeholder={addPlaceholder}
                />
                <button type="button" className={styles.addBtn} onClick={addImage}>
                    <Plus size={14} />
                    Add
                </button>
            </div>
        </div>
    )
}

export default MicrositeImageGalleryManager